import ApplicationController from './application_controller';

// The KIKI Helps panel docked at the bottom of the sidebar.
//
// This is not a modal. The page behind it stays live and usable, so there is no backdrop, no
// focus trap, and no inert content — the point of the feature is reading help while you carry
// on working.
//
// The trigger button lives in the nav tray, which is a different part of the DOM, so it is
// wired up by id rather than by Stimulus action scope. That is the same cross-DOM lookup
// dialog_controller.js does, just in the other direction.
//
// Open state is persisted for the session. Ambiki does full page loads, so without this the
// panel would slam shut on the first link the user clicked and "keep using the site" would
// mean nothing. The conversation body is fetched on first open and then trusted for an hour —
// reopening after that re-fetches, so a page left sitting cannot resurrect a conversation the
// server has already rolled over.
export default class extends ApplicationController {
  static targets = ['loading', 'content', 'newChat'];
  static values = {
    url: String,
    newChatUrl: String,
    triggerId: String,
    pageTitle: String,
  };

  static STORAGE_KEY = 'ai-help:open';

  // Mirrors GaiDiscussion::STALE_AFTER. Only matters on a page left open without
  // navigating: `loaded` would otherwise keep serving the cached thread forever, and the
  // server would never get the chance to roll a stale conversation over on open.
  static STALE_AFTER_MS = 60 * 60 * 1000;

  connect() {
    this.onTriggerClick = this.toggle.bind(this);
    this.onKeydown = this.handleKeydown.bind(this);
    this.onResize = this.aimArrow.bind(this);

    this.trigger?.addEventListener('click', this.onTriggerClick);
    document.addEventListener('keydown', this.onKeydown);
    window.addEventListener('resize', this.onResize);

    // Reopen after a navigation, but without stealing focus — the user asked for the new
    // page, not for the textarea.
    if (this.persistedOpen) this.open({ focus: false });
  }

  disconnect() {
    this.trigger?.removeEventListener('click', this.onTriggerClick);
    document.removeEventListener('keydown', this.onKeydown);
    window.removeEventListener('resize', this.onResize);
  }

  // Points the panel's arrow at the vertical centre of the KIKI Helps button.
  //
  // Measured rather than hard-coded: the button's position depends on the nav's padding, the
  // button's line-height and how many nav links the user can see, and pinning a guess in CSS
  // would drift the first time any of those changed. Clamped so the arrow can never ride up
  // over the panel's rounded corners.
  aimArrow() {
    const { trigger } = this;
    if (!trigger || !this.isOpen) return;

    const triggerRect = trigger.getBoundingClientRect();
    const panelRect = this.element.getBoundingClientRect();
    if (!panelRect.height) return;

    const centre = triggerRect.top + triggerRect.height / 2 - panelRect.top;
    const inset = 20;
    const clamped = Math.max(inset, Math.min(centre, panelRect.height - inset));
    this.element.style.setProperty('--ai-help-arrow-top', `${Math.round(clamped)}px`);
  }

  get trigger() {
    return this.hasTriggerIdValue ? document.getElementById(this.triggerIdValue) : null;
  }

  get isOpen() {
    return this.element.classList.contains('is-open');
  }

  get persistedOpen() {
    // Safari in private mode throws on sessionStorage access. Losing persistence is fine;
    // breaking the panel is not.
    try {
      return window.sessionStorage.getItem(this.constructor.STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  }

  persist(open) {
    // See persistedOpen — storage being unavailable is non-fatal.
    try {
      window.sessionStorage.setItem(this.constructor.STORAGE_KEY, open ? '1' : '0');
    } catch {
      // Nothing to do; the panel simply will not survive a navigation.
    }
  }

  handleKeydown(event) {
    // Escape closes the panel, but only when the user is working inside it. Elsewhere on the
    // page Escape belongs to whatever they are actually using.
    if (event.key !== 'Escape' || !this.isOpen) return;
    if (!this.element.contains(document.activeElement)) return;
    this.close();
    this.trigger?.focus();
  }

  toggle(event) {
    event?.preventDefault();
    if (this.isOpen) this.close();
    else this.open({ focus: true });
  }

  open({ focus = true } = {}) {
    this.element.classList.add('is-open');
    this.trigger?.setAttribute('aria-expanded', 'true');
    this.persist(true);
    // After is-open, since the panel has no measurable height while it is display:none.
    this.aimArrow();
    this.load({ focus });
  }

  close() {
    this.element.classList.remove('is-open');
    this.trigger?.setAttribute('aria-expanded', 'false');
    this.persist(false);
  }

  // A previously fetched conversation stops being trusted after STALE_AFTER_MS: the next
  // open re-fetches, and the server decides whether the thread is still live or gets rolled
  // over into a fresh one. Until the re-fetch lands the old markup stays visible, which is
  // also the graceful state if the re-fetch fails.
  get stale() {
    return Boolean(this.loadedAt) && Date.now() - this.loadedAt > this.constructor.STALE_AFTER_MS;
  }

  async load({ focus = true } = {}) {
    if (this.loaded && !this.stale) {
      if (focus) this.focusComposer();
      return;
    }
    if (this.loading) return;
    this.loading = true;

    try {
      const url = new URL(this.urlValue, window.location.origin);
      if (this.pageTitleValue) url.searchParams.set('page_title', this.pageTitleValue);

      const response = await fetch(url, {
        credentials: 'same-origin',
        headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      // An HTML request that fails authorization is redirected to root app-wide, and fetch
      // follows that silently and reports ok. Requiring the header the action sets is what
      // stops a full dashboard page from being injected here as if it were the conversation.
      if (response.headers.get('X-Ambiki-Ai-Help') !== '1') {
        throw new Error('unexpected response (not signed in, or KIKI Helps is off)');
      }

      this.contentTarget.innerHTML = await response.text();
      this.loaded = true;
      this.loadedAt = Date.now();
      if (this.hasLoadingTarget) this.loadingTarget.remove();
      if (focus) this.focusComposer();
    } catch (err) {
      // Surfaced in the console as well as the panel — a failure here is usually a 403 from
      // the flag being off, which is much easier to diagnose with the status visible.
      // eslint-disable-next-line no-console
      console.error('KIKI Helps failed to load', err);
      if (this.hasLoadingTarget) {
        this.loadingTarget.innerHTML =
          '<em class="text-danger"><i class="bi bi-exclamation-triangle mr-1"></i>' +
          "Couldn't load KIKI Helps. Close this and try again.</em>";
      }
    } finally {
      this.loading = false;
    }
  }

  // Swaps the conversation for a brand new one. Confirmed first: nothing is destroyed
  // server-side, but the user does lose sight of the thread they were in, and this button
  // sits a few pixels from Close.
  async newChat() {
    if (this.resetting) return;
    if (this.loaded && !window.confirm('Start a new chat? Your current conversation will be cleared.')) return;

    this.resetting = true;
    this.newChatTarget?.setAttribute('disabled', 'disabled');

    try {
      const url = new URL(this.newChatUrlValue, window.location.origin);
      if (this.pageTitleValue) url.searchParams.set('page_title', this.pageTitleValue);

      const response = await fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'text/html',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': this.csrfToken,
        },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      if (response.headers.get('X-Ambiki-Ai-Help') !== '1') {
        throw new Error('unexpected response (not signed in, or KIKI Helps is off)');
      }

      // Replacing the markup disconnects the old gai-chat controller and connects a fresh
      // one, so no in-flight reply from the previous conversation can land in this one.
      this.contentTarget.innerHTML = await response.text();
      this.loaded = true;
      this.loadedAt = Date.now();
      // Covers the edge case of a new chat started before the first load ever finished, which
      // would otherwise leave the spinner stranded above the fresh conversation.
      if (this.hasLoadingTarget) this.loadingTarget.remove();
      this.focusComposer();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('KIKI Helps could not start a new chat', err);
    } finally {
      this.resetting = false;
      this.newChatTarget?.removeAttribute('disabled');
    }
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || '';
  }

  focusComposer() {
    this.contentTarget.querySelector('textarea')?.focus();
  }
}
